/**
 * CLE-2811 — putting one document onto a contract model, shared by the run page's "Upload document" and
 * the "New contract model" dialog: reserve a row (presign), PUT the bytes straight to S3 with a deadline,
 * then ask the API to confirm the object is really there.
 *
 * Pure apart from the network calls it is handed, so the sequence is tested without a store.
 */

import { IPresignedUpload } from "store/queries/contractModelDrafts";

import { ALLOWED_UPLOAD_EXTENSIONS } from "./utils";

/** Ceiling on a single document upload. Contract PDFs/workbooks are small; a slow one is a broken one. */
export const UPLOAD_TIMEOUT_MS = 120_000;

/** The file's extension, lower-cased, without the dot; "" when it has none. */
export const fileExtension = (fileName: string): string => {
  const parts = fileName.toLowerCase().split(".");
  return parts.length > 1 ? parts[parts.length - 1] : "";
};

/** Whether clear-api accepts the file on a contract model (mirrors ALLOWED_EXTENSIONS in the backend). */
export const isAllowedUpload = (fileName: string): boolean =>
  ALLOWED_UPLOAD_EXTENSIONS.includes(fileExtension(fileName));

/** The `accept` attribute for a file input that takes contract-model documents. */
export const UPLOAD_ACCEPT = ALLOWED_UPLOAD_EXTENSIONS.map((e) => `.${e}`).join(
  ","
);

/** Why a file was refused before anything was sent. */
export const unsupportedFileDetail = (fileName: string): string =>
  `.${
    fileExtension(fileName) || "?"
  } isn't supported. Allowed: ${ALLOWED_UPLOAD_EXTENSIONS.join(", ")}.`;

const isAbort = (e: unknown): boolean =>
  e instanceof DOMException && e.name === "AbortError";

/** What went wrong with an upload, in words: the timeout, the API's message, the error's, or a fallback. */
export const uploadErrorDetail = (
  e: unknown,
  timeoutMs: number = UPLOAD_TIMEOUT_MS
): string => {
  if (isAbort(e))
    return `Upload timed out after ${
      timeoutMs / 1000
    }s. Check your connection and try again.`;
  const data = (e as { data?: { message?: unknown; error?: unknown } })?.data;
  if (typeof data?.message === "string" && data.message.length > 0)
    return data.message;
  // The contract attachment upload answers some refusals as `{ error }` rather than `{ message }`.
  if (typeof data?.error === "string" && data.error.length > 0)
    return data.error;
  if (e instanceof Error && e.message) return e.message;
  return "Could not upload the file.";
};

/** An RTK Query mutation trigger, as far as this module uses it. */
type ITrigger<A, R> = (arg: A) => { unwrap: () => Promise<R> };

export interface IUploadDocumentDeps {
  presign: ITrigger<
    { runId: string; fileName: string; contentType: string },
    IPresignedUpload
  >;
  confirm: ITrigger<{ runId: string; documentId: string }, unknown>;
  fetchImpl?: typeof fetch;
  timeoutMs?: number;
}

/**
 * presign → PUT → confirm. Resolves with the document id once the API has seen the object; rejects with
 * the reason otherwise (an AbortError when the PUT ran past the deadline — see `uploadErrorDetail`).
 *
 * A PUT that fails still leaves the row presign reserved, and a run holding a row with no object behind
 * it refuses to generate ("These uploads never completed") with no way to remove it from the page. So a
 * failed PUT is followed by a best-effort confirm, which deletes that row when S3 has nothing at its key.
 * If S3 does have the object (the deadline fired as the last byte landed), the confirm succeeds and the
 * upload counts as done — which is what it is.
 */
export async function uploadRunDocument(
  runId: string,
  file: File,
  {
    presign,
    confirm,
    fetchImpl = fetch,
    timeoutMs = UPLOAD_TIMEOUT_MS,
  }: IUploadDocumentDeps
): Promise<{ documentId: string }> {
  const contentType = file.type || "application/octet-stream";
  const { uploadUrl, documentId } = await presign({
    runId,
    fileName: file.name,
    contentType,
  }).unwrap();

  // A PUT straight to S3 has no built-in deadline: a stalled connection hangs forever, which is how an
  // upload could sit "in progress" indefinitely with the button stuck disabled.
  const abort = new AbortController();
  const timer = setTimeout(() => abort.abort(), timeoutMs);
  try {
    const res = await fetchImpl(uploadUrl, {
      method: "PUT",
      body: file,
      headers: { "Content-Type": contentType },
      signal: abort.signal,
    });
    if (!res.ok) throw new Error(`S3 rejected the upload (${res.status}).`);
  } catch (putError) {
    try {
      await confirm({ runId, documentId }).unwrap();
      return { documentId };
    } catch {
      throw putError;
    }
  } finally {
    clearTimeout(timer);
  }

  // Don't trust the PUT alone — ask the API to HEAD the object. This is what turns a silently failed
  // upload into an error you can see, instead of a run that dies 20 minutes later.
  await confirm({ runId, documentId }).unwrap();
  return { documentId };
}

/**
 * Run `worker` over `items`, at most `limit` at a time, in order of start. Resolves once every item has
 * settled, with each item's result (or the error it threw) in the items' order. Never rejects.
 */
export async function runWithConcurrency<T, R>(
  items: T[],
  limit: number,
  worker: (item: T) => Promise<R>
): Promise<({ ok: true; value: R } | { ok: false; error: unknown })[]> {
  const results: ({ ok: true; value: R } | { ok: false; error: unknown })[] =
    new Array(items.length);
  let next = 0;
  const lane = async (): Promise<void> => {
    while (next < items.length) {
      const index = next;
      next += 1;
      try {
        // eslint-disable-next-line no-await-in-loop
        results[index] = { ok: true, value: await worker(items[index]) };
      } catch (error) {
        results[index] = { ok: false, error };
      }
    }
  };
  await Promise.all(
    Array.from({ length: Math.max(1, Math.min(limit, items.length)) }, lane)
  );
  return results;
}
